import type { Opening, Plot, Point, PolygonPath, Space, StandardsTable } from '@/model/types'
import type { ParamValues } from '@/model/params'
import { closestPointOnPolygon } from '@/lib/geometry'
import { compoundCenterline } from '@/lib/walls'

/** Minimum jamb (wall left on each side of an opening) before placement is rejected, feet. */
export const JAMB_MARGIN = 0.25

// ---------------------------------------------------------------------------
// Param accessors — extract flat dimensions from an Opening's params bag
// ---------------------------------------------------------------------------

export function opWidth(op: Opening): number  { return Number(op.params.width  ?? 3) }
export function opHeight(op: Opening): number { return Number(op.params.height ?? 7) }
export function opSill(op: Opening): number   { return Number(op.params.sillHeight ?? 0) }
export function opKind(op: Opening): string   { return op.familyId }
export function opSwing(op: Opening): 'in' | 'out'       { return (op.params.swing as 'in' | 'out') ?? 'in' }
export function opHinge(op: Opening): 'left' | 'right'   { return (op.params.hinge as 'left' | 'right') ?? 'left' }
export function opKinematics(op: Opening): string         { return (op.params.kinematics as string) ?? 'swing' }
export function opLeaves(op: Opening): number             { return Number(op.params.leaves ?? 1) }

// ---------------------------------------------------------------------------

/** A local frame for an opening on a polygon edge — everything the symbols/cut need. */
export interface OpeningFrame {
  center: Point
  dir: Point
  normal: Point
  edgeLength: number
  angle: number
}

export function openingFrame(polygon: PolygonPath, edgeIndex: number, offset: number): OpeningFrame | null {
  const n = polygon.length
  if (n < 2) return null
  const a = polygon[((edgeIndex % n) + n) % n]
  const b = polygon[(edgeIndex + 1) % n]
  const dx = b.x - a.x
  const dy = b.y - a.y
  const len = Math.hypot(dx, dy)
  if (len < 1e-9) return null
  const dir = { x: dx / len, y: dy / len }
  const normal = { x: -dir.y, y: dir.x }
  return {
    center: { x: a.x + dir.x * offset, y: a.y + dir.y * offset },
    dir, normal, edgeLength: len,
    angle: Math.atan2(dir.y, dir.x),
  }
}

/**
 * The plan rectangle to subtract from the wall: spans `width` along the edge and
 * `perpThickness` across it (should generously exceed the wall so the gap fully crosses it).
 * Reads width from the opening's params bag.
 */
export function openingWorldRect(
  polygon: PolygonPath,
  opening: Opening,
  perpThickness: number,
): PolygonPath | null {
  const f = openingFrame(polygon, opening.edgeIndex, opening.offset)
  if (!f) return null
  const hw = opWidth(opening) / 2
  const hp = perpThickness / 2
  const { center: c, dir: d, normal: nrm } = f
  return [
    { x: c.x - d.x * hw - nrm.x * hp, y: c.y - d.y * hw - nrm.y * hp },
    { x: c.x + d.x * hw - nrm.x * hp, y: c.y + d.y * hw - nrm.y * hp },
    { x: c.x + d.x * hw + nrm.x * hp, y: c.y + d.y * hw + nrm.y * hp },
    { x: c.x - d.x * hw + nrm.x * hp, y: c.y - d.y * hw + nrm.y * hp },
  ]
}

export function clampOffset(
  polygon: PolygonPath,
  edgeIndex: number,
  width: number,
  offset: number,
): number | null {
  const f = openingFrame(polygon, edgeIndex, 0)
  if (!f) return null
  const min = width / 2 + JAMB_MARGIN
  const max = f.edgeLength - width / 2 - JAMB_MARGIN
  if (max < min) return null
  return Math.max(min, Math.min(max, offset))
}

export function offsetOnEdge(polygon: PolygonPath, edgeIndex: number, pt: Point): number {
  const f = openingFrame(polygon, edgeIndex, 0)
  if (!f) return 0
  const n = polygon.length
  const a = polygon[((edgeIndex % n) + n) % n]
  return (pt.x - a.x) * f.dir.x + (pt.y - a.y) * f.dir.y
}

export function buildingCutThickness(standards: StandardsTable): number {
  return standards.exteriorWallThickness * 1.2
}

export function spaceCutRects(spaces: Space[], standards: StandardsTable): PolygonPath[] {
  const perp = buildingCutThickness(standards)
  const rects: PolygonPath[] = []
  for (const sp of spaces) {
    for (const op of sp.openings ?? []) {
      const r = openingWorldRect(sp.polygon, op, perp)
      if (r) rects.push(r)
    }
  }
  return rects
}

export function compoundCutRects(plot: Plot): PolygonPath[] {
  const cw = plot.compoundWall
  if (!cw?.enabled) return []
  const centerline = compoundCenterline(plot.boundary, cw.inset)
  const perp = cw.thickness * 1.5
  const rects: PolygonPath[] = []
  for (const op of cw.openings ?? []) {
    const r = openingWorldRect(centerline, op, perp)
    if (r) rects.push(r)
  }
  return rects
}

export function openingCutRects(spaces: Space[], plot: Plot, standards: StandardsTable): PolygonPath[] {
  return [...spaceCutRects(spaces, standards), ...compoundCutRects(plot)]
}

// ---------------------------------------------------------------------------
// Defaults for freshly-placed openings (from standards)
// ---------------------------------------------------------------------------

/** Build the initial params for a freshly-placed opening of a given family. */
export function openingParamsFromPreset(presetParams: Partial<ParamValues>): ParamValues {
  return { ...presetParams } as ParamValues
}

/** The width to use for placement targeting (from resolved preset params + standards fallback). */
export function placementWidth(
  familyId: string,
  presetParams: Partial<ParamValues>,
  standards: StandardsTable,
  plot: Plot,
): number {
  if (typeof presetParams.width === 'number') return presetParams.width
  if (familyId === 'window') return standards.window.width
  if (familyId === 'gate') return plot.compoundWall?.openings?.[0] ? 10 : 10
  return standards.internalDoor.width
}

// ---------------------------------------------------------------------------
// Placement — find the nearest wall edge to a click
// ---------------------------------------------------------------------------

export interface OpeningTarget {
  ownerId: string
  polygon: PolygonPath
  edgeIndex: number
  offset: number
  dist: number
}

export function nearestOpeningTarget(
  pt: Point,
  familyId: string,
  width: number,
  spaces: Space[],
  plot: Plot,
): OpeningTarget | null {
  let best: OpeningTarget | null = null
  const consider = (ownerId: string, polygon: PolygonPath) => {
    if (polygon.length < 3) return
    const hit = closestPointOnPolygon(pt, polygon)
    const offset = clampOffset(polygon, hit.edgeIndex, width, offsetOnEdge(polygon, hit.edgeIndex, hit.point))
    if (offset === null) return
    if (!best || hit.dist < best.dist) {
      best = { ownerId, polygon, edgeIndex: hit.edgeIndex, offset, dist: hit.dist }
    }
  }

  if (familyId === 'gate') {
    const cw = plot.compoundWall
    if (cw?.enabled) consider('compound', compoundCenterline(plot.boundary, cw.inset))
  } else {
    for (const sp of spaces) {
      if (sp.wallMode === 'none') continue
      consider(sp.id, sp.polygon)
    }
  }
  return best
}
